import { Header } from "@made-people/blueprint-header";
import {
  Page,
  StoryblokStoryContentResponseV1,
} from "@made-people/nestjs-bff-service";
import { DesktopMenu } from "../features/menu/desktop-menu";
import { memo } from "react";
import { useRootLoaderData, gearsLoader } from "@made-people/gears-remix";
import { useRouteLoaderData } from "@remix-run/react";
import { mobileMenuOverlayId } from "../constants";
import {
  OldMenuProvider,
  useOldMenu,
} from "../features/old-menu/old-menu-context";
import { Topbar } from "./topbar";
import { SiteSettingsTriggerWithFlag } from "./site-settings-trigger-with-flag";

const MissMaryHeaders = memo(
  (props: {
    isHomePage: boolean;
    pageType: Page["pageType"];
    globalStoryblokStories: StoryblokStoryContentResponseV1[];
  }) => {
    const { isOpen } = useOldMenu();
    let color: "white" | "transparent" = "white";
    const isOverlay = props.isHomePage;
    if (props.isHomePage) {
      color = "transparent";
    }
    if (isOpen) {
      color = "white";
    }

    const isCheckout =
      props.pageType === "checkoutFinalize" ||
      props.pageType === "checkoutReceipt" ||
      props.pageType === "checkoutIndex";

    if (isCheckout) return null;

    return (
      <>
        <Header
          settings={{
            template: "template2",
            visibility: ["mobile", "tablet"],
            menuTrigger: {
              overlayId: mobileMenuOverlayId,
            },
            cartTrigger: {
              type: "link-to-checkout",
            },
            siteSettingsTrigger: {
              isEnabled: false,
            },
            topbar: (
              <Topbar globalStoryblokStories={props.globalStoryblokStories} />
            ),
            menu: (
              <DesktopMenu
                bgColor={color}
                globalStoryblokStories={props.globalStoryblokStories}
              />
            ),
          }}
        />

        <Header
          settings={{
            template: "barebone",
            visibility: "desktop",
            overlay: isOverlay,
            siteSettingsTrigger: {
              isEnabled: false,
            },
            variants: {
              color: color,
            },
            topbar: (
              <div className="relative">
                <Topbar globalStoryblokStories={props.globalStoryblokStories} />
                <div className="absolute first:hidden flex justify-end inset-0 max-w-component-max-w-xl box-content mx-auto px-component-spacing-xs md:px-component-spacing-md lg:px-component-spacing-md pointer-events-none">
                  <SiteSettingsTriggerWithFlag className="relative flex items-center gap-1 text-label-sm pointer-events-auto pt-1" />
                </div>
              </div>
            ),
            menu: (
              <DesktopMenu
                bgColor={color}
                globalStoryblokStories={props.globalStoryblokStories}
              />
            ),
          }}
        />
      </>
    );
  },
);

MissMaryHeaders.displayName = "MissMaryHeaders";

/**
 * Wrapper that listen on rootLoaderData and passes it to the MissMaryHeaders component,
 * to minimize re-renders of the router component
 */
const Headers = memo(() => {
  const rootLoaderData = useRootLoaderData();
  const loaderData = useRouteLoaderData<typeof gearsLoader>(
    "project-router-wildcard",
  );

  if (!loaderData) {
    return null;
  }

  const {
    pageResponse: { page },
  } = loaderData;

  const pageType = page.pageType;
  const isHomePage =
    pageType === "storyblokPage" && page.storyblokData.slug === "site";

  const globalStoryblokStories =
    rootLoaderData?.settingsAndContentResponse.globalStoryblokStories ?? [];

  return (
    <OldMenuProvider>
      <MissMaryHeaders
        isHomePage={isHomePage}
        pageType={pageType}
        globalStoryblokStories={globalStoryblokStories}
      />
    </OldMenuProvider>
  );
});

Headers.displayName = "Headers";

export { Headers };
